import { api } from "../api";
// Mensagens são exibidas centralmente via interceptor em service/api
import {
  PaymentPlan,
  AccountData,
  UpdateAccountRequest,
  CheckoutRequest,
  CheckoutResponse,
  BillingProduct,
  SubscriptionItem,
  CreateSubscriptionItemRequest,
  UpdateSubscriptionItemRequest,
  SubscriptionNextInvoice,
} from "../../types/auth";

let subscriptionItemsCache: SubscriptionItem[] | null = null;
let subscriptionItemsCacheAt = 0;
let pendingSubscriptionItemsRequest: Promise<SubscriptionItem[]> | null = null;
const SUBSCRIPTION_ITEMS_CACHE_TTL_MS = 3000;
let paymentPlansCache: PaymentPlan[] | null = null;
let paymentPlansCacheAt = 0;
let pendingPaymentPlansRequest: Promise<PaymentPlan[]> | null = null;
const PAYMENT_PLANS_CACHE_TTL_MS = 30000;
let billingProductsCache: BillingProduct[] | null = null;
let billingProductsCacheAt = 0;
let pendingBillingProductsRequest: Promise<BillingProduct[]> | null = null;
const BILLING_PRODUCTS_CACHE_TTL_MS = 30000;
let accountDataCache: AccountData | null = null;
let accountDataCacheAt = 0;
let pendingAccountDataRequest: Promise<AccountData> | null = null;
const ACCOUNT_DATA_CACHE_TTL_MS = 30000;
let nextInvoiceCache: SubscriptionNextInvoice | null = null;
let nextInvoiceCacheAt = 0;
let pendingNextInvoiceRequest: Promise<SubscriptionNextInvoice> | null = null;
const NEXT_INVOICE_CACHE_TTL_MS = 30000;

export const invalidateSubscriptionItemsCache = (): void => {
  subscriptionItemsCache = null;
  subscriptionItemsCacheAt = 0;
};

export const invalidatePaymentsCache = (): void => {
  paymentPlansCache = null;
  paymentPlansCacheAt = 0;
  pendingPaymentPlansRequest = null;
  billingProductsCache = null;
  billingProductsCacheAt = 0;
  pendingBillingProductsRequest = null;
  accountDataCache = null;
  accountDataCacheAt = 0;
  pendingAccountDataRequest = null;
  nextInvoiceCache = null;
  nextInvoiceCacheAt = 0;
  pendingNextInvoiceRequest = null;
};

// Funções da API para pagamento e planos
export const getPaymentPlans = async (): Promise<PaymentPlan[]> => {
  if (
    paymentPlansCache &&
    Date.now() - paymentPlansCacheAt < PAYMENT_PLANS_CACHE_TTL_MS
  ) {
    return paymentPlansCache;
  }

  if (pendingPaymentPlansRequest) {
    return pendingPaymentPlansRequest;
  }

  pendingPaymentPlansRequest = (async () => {
    try {
      const response = await api.get<PaymentPlan[]>("/billing/plans");
      paymentPlansCache = response.data;
      paymentPlansCacheAt = Date.now();
      return response.data;
    } catch (error: any) {
      throw error;
    } finally {
      pendingPaymentPlansRequest = null;
    }
  })();

  return pendingPaymentPlansRequest;
};

export const getAccountData = async (options?: {
  forceRefresh?: boolean;
}): Promise<AccountData> => {
  const forceRefresh = Boolean(options?.forceRefresh);

  if (
    !forceRefresh &&
    accountDataCache &&
    Date.now() - accountDataCacheAt < ACCOUNT_DATA_CACHE_TTL_MS
  ) {
    return accountDataCache;
  }

  if (!forceRefresh && pendingAccountDataRequest) {
    return pendingAccountDataRequest;
  }

  pendingAccountDataRequest = (async () => {
    try {
      const response = await api.get<AccountData>("/accounts/me");
      accountDataCache = response.data;
      accountDataCacheAt = Date.now();
      return response.data;
    } catch (error: any) {
      throw error;
    } finally {
      pendingAccountDataRequest = null;
    }
  })();

  return pendingAccountDataRequest;
};

export const updateAccountData = async (
  data: UpdateAccountRequest,
): Promise<AccountData> => {
  try {
    const response = await api.put<AccountData>("/accounts/me", data);
    accountDataCache = response.data;
    accountDataCacheAt = Date.now();
    return response.data;
  } catch (error: any) {
    // Erro: mensagem será exibida pelo interceptor (se presente no body)
    throw error;
  }
};

export const createCheckout = async (
  data: CheckoutRequest,
): Promise<CheckoutResponse> => {
  try {
    const response = await api.post<CheckoutResponse>(
      "/billing/subscription/checkout",
      data,
    );
    return response.data;
  } catch (error: any) {
    // Erro: mensagem será exibida pelo interceptor (se presente no body)
    throw error;
  }
};

export const getBillingProducts = async (): Promise<BillingProduct[]> => {
  if (
    billingProductsCache &&
    Date.now() - billingProductsCacheAt < BILLING_PRODUCTS_CACHE_TTL_MS
  ) {
    return billingProductsCache;
  }

  if (pendingBillingProductsRequest) {
    return pendingBillingProductsRequest;
  }

  pendingBillingProductsRequest = (async () => {
    try {
      const response = await api.get<BillingProduct[]>("/billing/products");
      billingProductsCache = response.data;
      billingProductsCacheAt = Date.now();
      return response.data;
    } catch (error: any) {
      throw error;
    } finally {
      pendingBillingProductsRequest = null;
    }
  })();

  return pendingBillingProductsRequest;
};

export const getSubscriptionItems = async (options?: {
  forceRefresh?: boolean;
}): Promise<SubscriptionItem[]> => {
  const forceRefresh = Boolean(options?.forceRefresh);
  if (
    !forceRefresh &&
    subscriptionItemsCache &&
    Date.now() - subscriptionItemsCacheAt < SUBSCRIPTION_ITEMS_CACHE_TTL_MS
  ) {
    return subscriptionItemsCache;
  }

  if (!forceRefresh && pendingSubscriptionItemsRequest) {
    return pendingSubscriptionItemsRequest;
  }

  pendingSubscriptionItemsRequest = (async () => {
    try {
      const response = await api.get<SubscriptionItem[]>(
        "/billing/subscription-items",
      );
      subscriptionItemsCache = response.data;
      subscriptionItemsCacheAt = Date.now();
      return response.data;
    } catch (error: any) {
      throw error;
    } finally {
      pendingSubscriptionItemsRequest = null;
    }
  })();

  return pendingSubscriptionItemsRequest;
};

export const getSubscriptionNextInvoice =
  async (): Promise<SubscriptionNextInvoice> => {
    if (
      nextInvoiceCache &&
      Date.now() - nextInvoiceCacheAt < NEXT_INVOICE_CACHE_TTL_MS
    ) {
      return nextInvoiceCache;
    }

    if (pendingNextInvoiceRequest) {
      return pendingNextInvoiceRequest;
    }

    pendingNextInvoiceRequest = (async () => {
      try {
        const response = await api.get<SubscriptionNextInvoice>(
          "/billing/subscription/next-invoice",
        );
        nextInvoiceCache = response.data;
        nextInvoiceCacheAt = Date.now();
        return response.data;
      } catch (error: any) {
        throw error;
      } finally {
        pendingNextInvoiceRequest = null;
      }
    })();

    return pendingNextInvoiceRequest;
  };

export const createSubscriptionItem = async (
  data: CreateSubscriptionItemRequest,
): Promise<SubscriptionItem> => {
  try {
    const response = await api.post<SubscriptionItem>(
      "/billing/subscription-items",
      data,
    );
    invalidateSubscriptionItemsCache();
    nextInvoiceCache = null;
    nextInvoiceCacheAt = 0;
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const updateSubscriptionItem = async (
  itemId: string,
  data: UpdateSubscriptionItemRequest,
): Promise<SubscriptionItem> => {
  try {
    const response = await api.put<SubscriptionItem>(
      `/billing/subscription-items/${itemId}`,
      data,
    );
    invalidateSubscriptionItemsCache();
    nextInvoiceCache = null;
    nextInvoiceCacheAt = 0;
    return response.data;
  } catch (error: any) {
    throw error;
  }
};
